import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { AppThunk, RootState } from 'store';
import { isPaymentMethodInvalid, isBillingAddressInvalid } from 'components/PaymentMethod/utils';
import createPaymentMethodApi from 'api/paymentMethod';
import { appContextSelector } from 'modules/app/appSlice';
import { setAvailablePaymentMethods, updateDefaultPaymentMethod as updatePaymentMethodInUserSlice } from 'modules/user/userSlice';
import createUserApi from 'api/user';

interface State {
  paymentMethod?: PaymentMethod;
  paymentMethods: PaymentMethod[];
  paymentMethodTypes: PaymentMethodTypes;
  isLoading: boolean;
}

const initialState: State = {
  isLoading: false,
  paymentMethodTypes: null,
  paymentMethods: [],
};

const slice = createSlice({
  name: 'payments',
  initialState,
  reducers: {
    setLoading: (state: State, action: PayloadAction<boolean>): State => {
      state.isLoading = action.payload;
      return state;
    },
    setPaymentMethod: (state: State, action: PayloadAction<PaymentMethod | undefined>): State => {
      state.paymentMethod = action.payload;
      return state;
    },
    updateInvoicePaymentMethod: (state: State, action: PayloadAction<PaymentMethod>): State => {
      const paymentMethodIdx = state.paymentMethods.findIndex(p => p.type === 'Invoice');
      state.paymentMethods[paymentMethodIdx] = action.payload;
      return state;
    },
    setPaymentMethods: (state: State, action: PayloadAction<PaymentMethod[]>): State => {
      state.paymentMethods = action.payload;
      return state;
    },
    setPaymentMethodTypes: (state: State, action: PayloadAction<PaymentMethodTypes>): State => {
      state.paymentMethodTypes = action.payload;
      return state;
    },
  },
});

const { setPaymentMethod, setPaymentMethods, setPaymentMethodTypes, setLoading, updateInvoicePaymentMethod } = slice.actions;

export { setPaymentMethod, setPaymentMethods, setPaymentMethodTypes, updateInvoicePaymentMethod };

export default slice.reducer;

export const updateDefaultPaymentMethod = (type: string, onError: () => void): AppThunk => async (dispatch, getState): Promise<void> => {
  const context = appContextSelector(getState());
  const paymentMethodApi = createPaymentMethodApi(context);
  const userApi = createUserApi(context);
  const { currentAccount } = getState().user;
  const { paymentMethods, paymentMethod } = getState().payments;
  const currentPaymentMethodWeight = paymentMethod?.weight ?? 0;
  const newPaymentMethod = paymentMethods?.find(e => e.type === type);
  const isInvoice = type === 'Invoice';
  const newPaymentMethodWeight =
    (newPaymentMethod?.weight ?? 0) > currentPaymentMethodWeight ? (newPaymentMethod?.weight ?? 0) + 1 : currentPaymentMethodWeight + 1;

  setLoading(true);

  try {
    if (newPaymentMethod) {
      if (!isInvoice) {
        await paymentMethodApi.updateDefaultPaymentMethod(
          currentAccount?.customerId ?? '',
          newPaymentMethod.id ?? newPaymentMethod.type?.toLowerCase() ?? '',
          newPaymentMethodWeight,
        );
      } else {
        const companyAddress = currentAccount?.customer?.info?.address;
        const savedBillingAddress = newPaymentMethod.invoice?.address;
        const billingAddress = savedBillingAddress ?? companyAddress;
        const result = await userApi.modifyBillingInformation(
          { ...newPaymentMethod.invoice, address: billingAddress, confirmed: true } as BillingInformation,
          newPaymentMethodWeight,
          currentAccount?.customerId ?? '',
          newPaymentMethod?.id ?? 'invoice',
        );
        dispatch(setPaymentMethod(result.payment?.defaultPaymentMethod));
        dispatch(updatePaymentMethodInUserSlice(result.payment?.defaultPaymentMethod));
        dispatch(setAvailablePaymentMethods(result.payment?.paymentMethods ?? []));
        dispatch(setPaymentMethods(result.payment?.paymentMethods ?? []));
        return;
      }
      dispatch(setPaymentMethod({ ...newPaymentMethod, weight: newPaymentMethodWeight }));
      dispatch(updatePaymentMethodInUserSlice({ ...newPaymentMethod, weight: newPaymentMethodWeight }));
      return;
    }

    setLoading(false);
  } catch (err) {
    onError?.();
    setLoading(false);
  }
};

export const isPaymentMethodInvalidSelector = createSelector(
  (state: RootState) =>
    state.app.context === 'connect'
      ? state.user.currentAccount?.partnership?.settings?.invoice
      : state.user.currentAccount?.customer?.payment?.defaultPaymentMethod,
  (state: RootState) => state.user.currentAccount?.customer?.payment?.invoiceOnly,
  (state: RootState) => state.app.theme === 'corporate',
  (state: RootState) =>
    state.user.currentAccount?.customer?.payment?.invoiceOnly ||
    state.payments.paymentMethodTypes === null ||
    state.payments.paymentMethodTypes?.includes('Invoice'),
  (state: RootState) =>
    state.payments.paymentMethod?.type === 'Invoice' ||
    (state.payments.paymentMethodTypes?.length === 1 && state.payments.paymentMethodTypes?.includes('Invoice')),
  (state: RootState) => state.app.context,
  (state: RootState) => state.user.currentAccount?.customer?.info?.address?.countryCodeIso2,
  (state: RootState) => state.user.currentAccount?.customer?.organizationNumber,
  (paymentMethod, isInvoice, isCorporateTheme, isInvoiceWeChooose, isInvoiceDefaultPaymentMethod, context, country, businessRegistrationNumber) => {
    return (
      ['wechooose', 'mychooose', 'connect'].includes(context) &&
      (isCorporateTheme ? true : !isInvoice) &&
      isPaymentMethodInvalid({ paymentMethod, context, isInvoiceWeChooose, isInvoiceDefaultPaymentMethod, country, businessRegistrationNumber })
    );
  },
);

export const isBillingAddressInvalidSelector = createSelector(
  (state: RootState) =>
    state.app.context === 'connect'
      ? state.user.currentAccount?.partnership?.settings?.invoice
      : state.user.currentAccount?.customer?.payment?.defaultPaymentMethod,
  (state: RootState) => state.user.currentAccount?.customer?.payment?.invoiceOnly,
  (state: RootState) => state.app.theme === 'corporate',
  (state: RootState) =>
    state.user.currentAccount?.customer?.payment?.invoiceOnly ||
    state.payments.paymentMethodTypes === null ||
    state.payments.paymentMethodTypes?.includes('Invoice'),
  (state: RootState) =>
    state.payments.paymentMethod?.type === 'Invoice' ||
    (state.payments.paymentMethodTypes?.length === 1 && state.payments.paymentMethodTypes?.includes('Invoice')),
  (state: RootState) => state.app.context,
  (state: RootState) => state.user.currentAccount?.customer?.info?.address?.countryCodeIso2,
  (paymentMethod, isInvoice, isCorporateTheme, isInvoiceWeChooose, isInvoiceDefaultPaymentMethod, context, country) => {
    return (
      ['wechooose', 'mychooose', 'connect'].includes(context) &&
      (isCorporateTheme ? true : !isInvoice) &&
      isBillingAddressInvalid({ paymentMethod, context, isInvoiceWeChooose, isInvoiceDefaultPaymentMethod, country })
    );
  },
);

export const isPaymentMethodMissingSelector = createSelector(
  (state: RootState) =>
    state.app.context === 'connect'
      ? state.user.currentAccount?.partnership?.settings?.invoice
      : state.user.currentAccount?.customer?.payment?.defaultPaymentMethod,
  (state: RootState) => state.user.currentAccount?.customer?.payment?.invoiceOnly,
  (state: RootState) => state.app.theme === 'corporate',
  (state: RootState) => state.app.context,
  (paymentMethod, isInvoice, isCorporateTheme, context) =>
    ['wechooose', 'mychooose', 'connect'].includes(context) && (isCorporateTheme ? true : !isInvoice) && !paymentMethod,
);

export const isInvoicePaymentMethodConfirmedSelector = createSelector(
  (state: RootState) =>
    state.user.currentAccount?.customer?.payment?.invoiceOnly ||
    (state.payments.paymentMethodTypes?.length === 1 && state.payments.paymentMethodTypes?.includes('Invoice')) ||
    state.payments.paymentMethod?.type === 'Invoice',
  (state: RootState) => state.payments.paymentMethod?.invoice?.confirmed,
  (state: RootState) => state.app.context,
  (isInvoiceDefaultPaymentMethod, isConfirmed, context) => (context === 'wechooose' && isInvoiceDefaultPaymentMethod ? isConfirmed : true),
);
